import { AbstractModel } from '@/models/abstractModel';
import { BetSelection, EventFeedType, EventPeriod, Jollywhere, LeagueLimitsCategory, Line } from '@/constants';
import { ILiveFeedDataAPI, IPregameFeedDataAPI } from '@/types/feed';
import { Ref, ref } from 'vue';
import { BetItem } from '@/models/bet';
import { useLeagueFavorite } from '@/composables/useLeagueFavorite';
import { useTeamFavorite } from '@/composables/useTeamFavorite';
import { useEventFavorite } from '@/composables/useEventFavorite';
import { useEventStore } from '@/stores/event';
import { useLeagueStore } from '@/stores/league';
import { useTeamStore } from '@/stores/team';
import { AppError } from '@/errors/appError';
import { calculatePosition } from '@/helpers';

export abstract class AbstractFeed extends AbstractModel {
    public periodTypeId: EventPeriod;

    public uniqKey: string;
    public eventFeedType: Ref<EventFeedType | null>;
    public isNewGroupStarted: Ref<number>;
    public line: Ref<Line>;
    public eventStartedAt: Ref<string | null>;
    public homeWinPercClose: Ref<number | null>;
    public awayWinPercClose: Ref<number | null>;
    public customEventId: Ref<string>;
    public leagueId: Ref<number>;
    public leagueName: Ref<string>;
    public limitsCategoryId: Ref<LeagueLimitsCategory>;
    public leagueRank: Ref<number | null>;
    public iso3: Ref<string | null>;
    public teamHomeId: Ref<number>;
    public teamHomeName: Ref<string>;
    public teamHomeLogo: Ref<string | null>;
    public teamAwayId: Ref<number>;
    public teamAwayName: Ref<string>;
    public teamAwayLogo: Ref<string | null>;

    // WLD markets
    public wldHomePrice: Ref<string | null>;
    public wldAwayPrice: Ref<string | null>;
    public wldDrawPrice: Ref<string | null>;
    public wldHomeBookmakerId: Ref<number | null>;
    public wldAwayBookmakerId: Ref<number | null>;
    public wldDrawBookmakerId: Ref<number | null>;
    public wldHomePriceList: [number, number][] | null;
    public wldAwayPriceList: [number, number][] | null;
    public wldDrawPriceList: [number, number][] | null;
    public wldFeedCount: Ref<number | null>;
    public wldMarginPerc: Ref<string | null>;

    // BTS markets
    public btsYesPrice: Ref<string | null>;
    public btsNoPrice: Ref<string | null>;
    public btsYesBookmakerId: Ref<number | null>;
    public btsNoBookmakerId: Ref<number | null>;
    public btsYesPriceList: [number, number][] | null;
    public btsNoPriceList: [number, number][] | null;
    public btsFeedCount: Ref<number | null>;

    // DC markets
    public dc1XPrice: Ref<string | null>;
    public dcX2Price: Ref<string | null>;
    public dc12Price: Ref<string | null>;
    public dc1XBookmakerId: Ref<number | null>;
    public dcX2BookmakerId: Ref<number | null>;
    public dc12BookmakerId: Ref<number | null>;
    public dc1XPriceList: [number, number][] | null;
    public dcX2PriceList: [number, number][] | null;
    public dc12PriceList: [number, number][] | null;
    public dcFeedCount: Ref<number | null>;

    // Handicap (HDP)
    public jollywhere: Ref<Jollywhere | null>;
    public handicap: Ref<string | null>;
    public handicapHomePrice: Ref<string | null>;
    public handicapAwayPrice: Ref<string | null>;
    public handicapHomeBookmakerId: Ref<number | null>;
    public handicapAwayBookmakerId: Ref<number | null>;
    public handicapHomePriceList: [number, number][] | null;
    public handicapAwayPriceList: [number, number][] | null;
    public handicapFeedCount: Ref<number | null>;
    public handicapMarginPerc: Ref<string | null>;

    // Over/Under (OU)
    public goalHandicap: Ref<string | null>;
    public goalOverPrice: Ref<string | null>;
    public goalUnderPrice: Ref<string | null>;
    public goalOverBookmakerId: Ref<number | null>;
    public goalUnderBookmakerId: Ref<number | null>;
    public goalOverPriceList: [number, number][] | null;
    public goalUnderPriceList: [number, number][] | null;
    public goalFeedCount: Ref<number | null>;
    public goalMarginPerc: Ref<string | null>;

    private highlightClassIndex: Record<number, number> = {};
    private previousPrices: Record<number, string | null> = {};

    public position: Ref<string | null> = ref(null);

    protected constructor(periodTypeId: EventPeriod, data: ILiveFeedDataAPI | IPregameFeedDataAPI) {
        super();

        this.periodTypeId = periodTypeId;

        this.eventFeedType = ref(data.mId ?? null);
        this.uniqKey = data.ceId + '_' + data.line;
        this.isNewGroupStarted = ref(0); // we change it in stores, during filtering
        this.line = ref(data.line);
        this.eventStartedAt = ref(data.dg ?? null);
        this.homeWinPercClose = ref('clHWinPerc' in data ? (data.clHWinPerc ?? null) : 'hWinPerc' in data ? (data.hWinPerc ?? null) : null);
        this.awayWinPercClose = ref('clAWinPerc' in data ? (data.clAWinPerc ?? null) : 'aWinPerc' in data ? (data.aWinPerc ?? null) : null);
        this.customEventId = ref(data.ceId);
        this.leagueId = ref(data.clId);
        this.leagueName = ref(data.clName!);
        this.limitsCategoryId = ref(data.clLCId!);
        this.leagueRank = ref(data.clR ?? null);
        this.iso3 = ref(data.iso3 ?? null);
        this.teamHomeId = ref(data.cthId);
        this.teamHomeName = ref(data.cthName!);
        this.teamHomeLogo = ref(data.cthLogo ?? null);
        this.teamAwayId = ref(data.ctaId);
        this.teamAwayName = ref(data.ctaName!);
        this.teamAwayLogo = ref(data.ctaLogo ?? null);

        this.wldHomePrice = ref(data.wldHm ?? null);
        this.previousPrices[BetSelection.OneXTwoHome] = data.wldHm ?? null;
        this.wldAwayPrice = ref(data.wldAm ?? null);
        this.previousPrices[BetSelection.OneXTwoAway] = data.wldAm ?? null;
        this.wldDrawPrice = ref(data.wldDm ?? null);
        this.previousPrices[BetSelection.OneXTwoDraw] = data.wldDm ?? null;
        this.wldHomeBookmakerId = ref(data.wldHmS ?? null);
        this.wldAwayBookmakerId = ref(data.wldAmS ?? null);
        this.wldDrawBookmakerId = ref(data.wldDmS ?? null);
        this.wldHomePriceList = data.wldHList ?? null;
        this.wldAwayPriceList = data.wldAList ?? null;
        this.wldDrawPriceList = data.wldDList ?? null;
        this.wldFeedCount = ref(data.FCntWld ?? null);
        this.wldMarginPerc = ref(data.wldMPerc ?? null);

        this.btsYesPrice = ref(data.btsYm ?? null);
        this.previousPrices[BetSelection.BtsYes] = data.btsYm ?? null;
        this.btsNoPrice = ref(data.btsNm ?? null);
        this.previousPrices[BetSelection.BtsNo] = data.btsNm ?? null;
        this.btsYesBookmakerId = ref(data.btsYmS ?? null);
        this.btsNoBookmakerId = ref(data.btsNmS ?? null);
        this.btsYesPriceList = data.btsYList ?? null;
        this.btsNoPriceList = data.btsNList ?? null;
        this.btsFeedCount = ref(data.FCntBts ?? null);

        this.dc1XPrice = ref(data.dc1Xm ?? null);
        this.previousPrices[BetSelection.Dc1X] = data.dc1Xm ?? null;
        this.dcX2Price = ref(data.dcX2m ?? null);
        this.previousPrices[BetSelection.DcX2] = data.dcX2m ?? null;
        this.dc12Price = ref(data.dc12m ?? null);
        this.previousPrices[BetSelection.Dc12] = data.dc12m ?? null;
        this.dc1XBookmakerId = ref(data.dc1XmS ?? null);
        this.dcX2BookmakerId = ref(data.dcX2mS ?? null);
        this.dc12BookmakerId = ref(data.dc12mS ?? null);
        this.dc1XPriceList = data.dc1XList ?? null;
        this.dcX2PriceList = data.dcX2List ?? null;
        this.dc12PriceList = data.dc12List ?? null;
        this.dcFeedCount = ref(data.FCntDc ?? null);

        this.jollywhere = ref(data.jw ?? null);
        this.handicap = ref(data.hdV ?? null);
        this.handicapHomePrice = ref(data.hdK1m ?? null);
        this.previousPrices[BetSelection.HandicapHome] = data.hdK1m ?? null;
        this.handicapAwayPrice = ref(data.hdK2m ?? null);
        this.previousPrices[BetSelection.HandicapAway] = data.hdK2m ?? null;
        this.handicapHomeBookmakerId = ref(data.hdK1mS ?? null);
        this.handicapAwayBookmakerId = ref(data.hdK2mS ?? null);
        this.handicapHomePriceList = data.hdK1List ?? null;
        this.handicapAwayPriceList = data.hdK2List ?? null;
        this.handicapFeedCount = ref(data.FCntHdp ?? null);
        this.handicapMarginPerc = ref(data.hdpMPerc ?? null);

        this.goalHandicap = ref(data.ouV ?? null);
        this.goalOverPrice = ref(data.ouK1m ?? null);
        this.previousPrices[BetSelection.GoalOver] = data.ouK1m ?? null;
        this.goalUnderPrice = ref(data.ouK2m ?? null);
        this.previousPrices[BetSelection.GoalUnder] = data.ouK2m ?? null;
        this.goalOverBookmakerId = ref(data.ouK1mS ?? null);
        this.goalUnderBookmakerId = ref(data.ouK2mS ?? null);
        this.goalOverPriceList = data.ouK1List ?? null;
        this.goalUnderPriceList = data.ouK2List ?? null;
        this.goalFeedCount = ref(data.FCntOu ?? null);
        this.goalMarginPerc = ref(data.ouMPerc ?? null);
    }

    public update(data: Partial<ILiveFeedDataAPI>): void {
        if (data.mId !== undefined) this.eventFeedType.value = data.mId;
        if (data.line !== undefined) this.line.value = data.line;
        if (data.dg !== undefined) this.eventStartedAt.value = data.dg;
        if (data.clHWinPerc !== undefined) this.homeWinPercClose.value = data.clHWinPerc;
        if (data.clAWinPerc !== undefined) this.awayWinPercClose.value = data.clAWinPerc;
        if (data.ceId !== undefined) this.customEventId.value = data.ceId;
        if (data.clId !== undefined) this.leagueId.value = data.clId;
        if (data.clName !== undefined) this.leagueName.value = data.clName;
        if (data.clLCId !== undefined) this.limitsCategoryId.value = data.clLCId;
        if (data.clR !== undefined) this.leagueRank.value = data.clR;
        if (data.iso3 !== undefined) this.iso3.value = data.iso3;
        if (data.cthId !== undefined) this.teamHomeId.value = data.cthId;
        if (data.cthName !== undefined) this.teamHomeName.value = data.cthName;
        if (data.cthLogo !== undefined) this.teamHomeLogo.value = data.cthLogo;
        if (data.ctaId !== undefined) this.teamAwayId.value = data.ctaId;
        if (data.ctaName !== undefined) this.teamAwayName.value = data.ctaName;
        if (data.ctaLogo !== undefined) this.teamAwayLogo.value = data.ctaLogo;

        if (data.wldHm !== undefined) {
            this.previousPrices[BetSelection.OneXTwoHome] = this.wldHomePrice.value;
            this.wldHomePrice.value = data.wldHm;
        }
        if (data.wldAm !== undefined) {
            this.previousPrices[BetSelection.OneXTwoAway] = this.wldAwayPrice.value;
            this.wldAwayPrice.value = data.wldAm;
        }
        if (data.wldDm !== undefined) {
            this.previousPrices[BetSelection.OneXTwoDraw] = this.wldDrawPrice.value;
            this.wldDrawPrice.value = data.wldDm;
        }
        if (data.wldHmS !== undefined) this.wldHomeBookmakerId.value = data.wldHmS;
        if (data.wldAmS !== undefined) this.wldAwayBookmakerId.value = data.wldAmS;
        if (data.wldDmS !== undefined) this.wldDrawBookmakerId.value = data.wldDmS;
        if (data.wldHList !== undefined) this.wldHomePriceList = data.wldHList;
        if (data.wldAList !== undefined) this.wldAwayPriceList = data.wldAList;
        if (data.wldDList !== undefined) this.wldDrawPriceList = data.wldDList;
        if (data.FCntWld !== undefined) this.wldFeedCount.value = data.FCntWld;
        if (data.wldMPerc !== undefined) this.wldMarginPerc.value = data.wldMPerc;

        if (data.btsYm !== undefined) {
            this.previousPrices[BetSelection.BtsYes] = this.btsYesPrice.value;
            this.btsYesPrice.value = data.btsYm;
        }
        if (data.btsNm !== undefined) {
            this.previousPrices[BetSelection.BtsNo] = this.btsNoPrice.value;
            this.btsNoPrice.value = data.btsNm;
        }
        if (data.btsYmS !== undefined) this.btsYesBookmakerId.value = data.btsYmS;
        if (data.btsNmS !== undefined) this.btsNoBookmakerId.value = data.btsNmS;
        if (data.btsYList !== undefined) this.btsYesPriceList = data.btsYList;
        if (data.btsNList !== undefined) this.btsNoPriceList = data.btsNList;
        if (data.FCntBts !== undefined) this.btsFeedCount.value = data.FCntBts;

        if (data.dc1Xm !== undefined) {
            this.previousPrices[BetSelection.Dc1X] = this.dc1XPrice.value;
            this.dc1XPrice.value = data.dc1Xm;
        }
        if (data.dcX2m !== undefined) {
            this.previousPrices[BetSelection.DcX2] = this.dcX2Price.value;
            this.dcX2Price.value = data.dcX2m;
        }
        if (data.dc12m !== undefined) {
            this.previousPrices[BetSelection.Dc12] = this.dc12Price.value;
            this.dc12Price.value = data.dc12m;
        }
        if (data.dc1XmS !== undefined) this.dc1XBookmakerId.value = data.dc1XmS;
        if (data.dcX2mS !== undefined) this.dcX2BookmakerId.value = data.dcX2mS;
        if (data.dc12mS !== undefined) this.dc12BookmakerId.value = data.dc12mS;
        if (data.dc1XList !== undefined) this.dc1XPriceList = data.dc1XList;
        if (data.dcX2List !== undefined) this.dcX2PriceList = data.dcX2List;
        if (data.dc12List !== undefined) this.dc12PriceList = data.dc12List;
        if (data.FCntDc !== undefined) this.dcFeedCount.value = data.FCntDc;

        if (data.jw !== undefined) this.jollywhere.value = data.jw;
        if (data.hdV !== undefined) this.handicap.value = data.hdV;
        if (data.hdK1m !== undefined) {
            this.previousPrices[BetSelection.HandicapHome] = this.handicapHomePrice.value;
            this.handicapHomePrice.value = data.hdK1m;
        }
        if (data.hdK2m !== undefined) {
            this.previousPrices[BetSelection.HandicapAway] = this.handicapAwayPrice.value;
            this.handicapAwayPrice.value = data.hdK2m;
        }
        if (data.hdK1mS !== undefined) this.handicapHomeBookmakerId.value = data.hdK1mS;
        if (data.hdK2mS !== undefined) this.handicapAwayBookmakerId.value = data.hdK2mS;
        if (data.hdK1List !== undefined) this.handicapHomePriceList = data.hdK1List;
        if (data.hdK2List !== undefined) this.handicapAwayPriceList = data.hdK2List;
        if (data.FCntHdp !== undefined) this.handicapFeedCount.value = data.FCntHdp;
        if (data.hdpMPerc !== undefined) this.handicapMarginPerc.value = data.hdpMPerc;

        if (data.ouV !== undefined) this.goalHandicap.value = data.ouV;
        if (data.ouK1m !== undefined) {
            this.previousPrices[BetSelection.GoalOver] = this.goalOverPrice.value;
            this.goalOverPrice.value = data.ouK1m;
        }
        if (data.ouK2m !== undefined) {
            this.previousPrices[BetSelection.GoalUnder] = this.goalUnderPrice.value;
            this.goalUnderPrice.value = data.ouK2m;
        }
        if (data.ouK1mS !== undefined) this.goalOverBookmakerId.value = data.ouK1mS;
        if (data.ouK2mS !== undefined) this.goalUnderBookmakerId.value = data.ouK2mS;
        if (data.ouK1List !== undefined) this.goalOverPriceList = data.ouK1List;
        if (data.ouK2List !== undefined) this.goalUnderPriceList = data.ouK2List;
        if (data.FCntOu !== undefined) this.goalFeedCount.value = data.FCntOu;
        if (data.ouMPerc !== undefined) this.goalMarginPerc.value = data.ouMPerc;
    }

    public isHomeTeamProcessing(): boolean {
        const store = useTeamStore();
        return this.teamHomeId.value in store.favoriteTeamProcessing;
    }

    public isHomeTeamFavorite(): boolean {
        const store = useTeamStore();
        return this.teamHomeId.value in store.favoriteTeam;
    }

    public toggleHomeTeamFavorite(): void {
        const { toggleTeamFavorite } = useTeamFavorite();
        toggleTeamFavorite(this.teamHomeId.value);
    }

    public isAwayTeamProcessing(): boolean {
        const store = useTeamStore();
        return this.teamAwayId.value in store.favoriteTeamProcessing;
    }

    public isAwayTeamFavorite(): boolean {
        const store = useTeamStore();
        return this.teamAwayId.value in store.favoriteTeam;
    }

    public toggleAwayTeamFavorite(): void {
        const { toggleTeamFavorite } = useTeamFavorite();
        toggleTeamFavorite(this.teamAwayId.value);
    }

    public isLeagueProcessing(): boolean {
        const store = useLeagueStore();
        return this.leagueId.value in store.favoriteLeagueProcessing;
    }

    public isLeagueFavorite(): boolean {
        const store = useLeagueStore();
        return this.leagueId.value in store.favoriteLeague;
    }

    public toggleLeagueFavorite(): void {
        const { toggleLeagueFavorite } = useLeagueFavorite();
        toggleLeagueFavorite(this.leagueId.value);
    }

    public isEventProcessing(): boolean {
        const store = useEventStore();
        return this.customEventId.value in store.favoriteEventProcessing;
    }

    public isEventFavorite(): boolean {
        const store = useEventStore();
        return this.customEventId.value in store.favoriteEvent;
    }

    public toggleEventFavorite(): void {
        const { toggleEventFavorite } = useEventFavorite();
        toggleEventFavorite(this.customEventId.value);
    }

    public getHighlightClass(betSelection: BetSelection, price: string): string {
        //console.log('getHighlightClass execute for BetSelection #' + betSelection);
        const hlClassIndex = (this.highlightClassIndex[betSelection] ?? 2) === 2 ? 1 : 2;
        this.highlightClassIndex[betSelection] = hlClassIndex;
        const previousPrice = this.previousPrices[betSelection];

        if (previousPrice !== null) {
            if (price > previousPrice) {
                //need to reset prev price, to prevent highlighting on next re-render
                this.previousPrices[betSelection] = null;
                return `highlightGreen${hlClassIndex}`;
            } else if (price < previousPrice) {
                //need to reset prev price, to prevent highlighting on next re-render
                this.previousPrices[betSelection] = null;
                return `highlightRed${hlClassIndex}`;
            }
        }

        return '';
    }

    public abstract getEventScoreForBet(bet: BetItem): { home: number | null; away: number | null };

    public calculatePosition(): void {
        const totalBetBalance = calculatePosition(this.customEventId.value, this.periodTypeId, (bet) => this.getEventScoreForBet(bet));

        if (totalBetBalance !== null) {
            this.position.value = totalBetBalance.toFixed(2);
        } else {
            this.position.value = null;
        }
    }

    public getMarketDataByBetSelection(betSelectionId: BetSelection) {
        switch (betSelectionId) {
            // 1X2 markets
            case BetSelection.OneXTwoHome:
                return {
                    handicap: this.handicap.value,
                    jollywhere: this.jollywhere.value,
                    price: this.wldHomePrice,
                    priceList: this.wldHomePriceList,
                };
            case BetSelection.OneXTwoDraw:
                return {
                    handicap: this.handicap.value,
                    jollywhere: this.jollywhere.value,
                    price: this.wldDrawPrice,
                    priceList: this.wldDrawPriceList,
                };
            case BetSelection.OneXTwoAway:
                return {
                    handicap: this.handicap.value,
                    jollywhere: this.jollywhere.value,
                    price: this.wldAwayPrice,
                    priceList: this.wldAwayPriceList,
                };

            // Handicap markets
            case BetSelection.HandicapHome:
                return {
                    handicap: this.handicap.value,
                    jollywhere: this.jollywhere.value,
                    price: this.handicapHomePrice,
                    priceList: this.handicapHomePriceList,
                };
            case BetSelection.HandicapAway:
                return {
                    handicap: this.handicap.value,
                    jollywhere: this.jollywhere.value,
                    price: this.handicapAwayPrice,
                    priceList: this.handicapAwayPriceList,
                };

            // Goal (Over/Under) markets
            case BetSelection.GoalOver:
                return {
                    handicap: this.goalHandicap.value,
                    jollywhere: null,
                    price: this.goalOverPrice,
                    priceList: this.goalOverPriceList,
                };
            case BetSelection.GoalUnder:
                return {
                    handicap: this.goalHandicap.value,
                    jollywhere: null,
                    price: this.goalUnderPrice,
                    priceList: this.goalUnderPriceList,
                };

            // BTS markets
            case BetSelection.BtsYes:
                return {
                    handicap: null,
                    jollywhere: this.jollywhere.value,
                    price: this.btsYesPrice,
                    priceList: this.btsYesPriceList,
                };
            case BetSelection.BtsNo:
                return {
                    handicap: null,
                    jollywhere: this.jollywhere.value,
                    price: this.btsNoPrice,
                    priceList: this.btsNoPriceList,
                };

            // Double Chance markets
            case BetSelection.Dc1X:
                return {
                    handicap: null,
                    jollywhere: this.jollywhere.value,
                    price: this.dc1XPrice,
                    priceList: this.dc1XPriceList,
                };
            case BetSelection.Dc12:
                return {
                    handicap: null,
                    jollywhere: this.jollywhere.value,
                    price: this.dc12Price,
                    priceList: this.dc12PriceList,
                };
            case BetSelection.DcX2:
                return {
                    handicap: null,
                    jollywhere: this.jollywhere.value,
                    price: this.dcX2Price,
                    priceList: this.dcX2PriceList,
                };

            default:
                throw new AppError(`Unknown betSelectionId ${betSelectionId}`);
        }
    }
}
